/**
 * 地址搜索 Service —— AddressPicker 与后端地图 Web 服务的唯一接触面
 *
 * 能力三件套（后端 /api/map/* 受保护端点，Key 只在服务端）：
 *   searchAddress(keyword, region) 地名搜索 → POI 列表（坐标 GCJ-02 canonical）
 *   reverseGeocode(lat, lng)       坐标(GCJ-02) → 地址文本（GPS 定位回填）
 *   isSearchUnavailable(r)         后端响应不可用判定（search_not_configured → 调用方收起搜索 UI）
 *
 * 厂商隔离：本文件零厂商 API——天地图调用与 CGCS2000↔GCJ-02 转换全在后端服务边界。
 * 降级铁律：任何失败返回 { success:false }，地址编辑回退纯手填，绝不卡死保存。
 */
import axios from 'axios'

/**
 * 地名搜索
 * @param {string} keyword 搜索词（≥2 字符才有意义，调用方 debounce）
 * @param {string} [region] 行政区名称（已选城市，提高精度；可空）
 * @returns {Promise<{success:boolean, unavailable?:boolean, results?:Array<{name,address,province,city,latitude,longitude}>}>}
 */
export async function searchAddress (keyword, region = '') {
  const kw = String(keyword || '').trim()
  if (!kw) return { success: false, results: [] }
  try {
    const res = await axios.get('/map/search', { params: { keyword: kw, region: region || undefined } })
    const d = (res && res.data && res.data.data) || {}
    if (d.success && Array.isArray(d.results)) {
      return {
        success: true,
        results: d.results.filter((p) => p && p.name && p.latitude != null).map((p) => ({
          name: String(p.name),
          address: String(p.address || ''),
          province: String(p.province || ''),
          city: String(p.city || ''),
          latitude: Number(p.latitude),
          longitude: Number(p.longitude)
        }))
      }
    }
    // 后端未配置地图 Web 服务 → 标记 unavailable，调用方收起搜索 UI（§8：手填为主）
    return { success: false, unavailable: d.reason === 'search_not_configured', results: [] }
  } catch (e) {
    return { success: false, results: [] }
  }
}

/**
 * 逆地理编码（坐标 → 地址文本）
 * @param {number} lat GCJ-02 纬度
 * @param {number} lng GCJ-02 经度
 * @returns {Promise<{success:boolean, unavailable?:boolean, formatted_address?:string}>}
 */
export async function reverseGeocode (lat, lng) {
  try {
    const res = await axios.get('/map/reverse-geocode', { params: { lat, lng } })
    const d = (res && res.data && res.data.data) || {}
    if (d.success && d.formatted_address) {
      return { success: true, formatted_address: String(d.formatted_address) }
    }
    return { success: false, unavailable: d.reason === 'search_not_configured' }
  } catch (e) {
    return { success: false }
  }
}
